// DB-Portal – Memo-Werkzeuge (Phase E): Entgeltreihe, Verjährungsfenster, Rückforderungsschätzung,
// Einschätzungsreife und Benchmark je Gebiet. Alles sind Rechenhilfen mit sichtbaren Annahmen,
// keine Rechtsauskunft; maßgeblich bleiben die Originalunterlagen.
import { z } from 'zod';
import { dbPortalDokumentArt } from './db-portal-lieferung.js';
function istKalenderdatum(d) {
    const [j, m, t] = d.split('-').map(Number);
    const x = new Date(Date.UTC(j, m - 1, t));
    return x.getUTCFullYear() === j && x.getUTCMonth() === m - 1 && x.getUTCDate() === t;
}
/** ISO-Datum JJJJ-MM-TT, gültiges Kalenderdatum */
const isoDatum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(istKalenderdatum, 'kein gültiges Kalenderdatum');
/** Eingaben zusätzlich 1990–2100: größere Jahre verfälschten Fristvergleiche und Monatslisten (Review E, P3-4) */
const eingabeDatum = isoDatum.refine((d) => d >= '1990-01-01' && d <= '2100-12-31', 'Datum außerhalb 1990–2100');
const isoMonat = z.string().regex(/^\d{4}-\d{2}$/);
/** Kenntnis vom Anspruch (§ 199 Abs. 1 Nr. 2 BGB, § 33h Abs. 2 Nr. 2 GWB): mit der Zahlung, zu einem festen Datum, unbekannt */
export const dbPortalKenntnis = z.enum(['zahlung', 'datum', 'unbekannt']);
/** Verstoß je Zahlung beendet (einzel) oder Dauerverstoß, der erst mit „beendigung“ endet (§ 33h Abs. 2 Nr. 3, Abs. 3 Nr. 2 GWB) */
export const dbPortalVerstoss = z.enum(['einzel', 'dauer']);
export const dbPortalAnalyseParameter = z.object({
    stichtag: eingabeDatum,
    kenntnis: dbPortalKenntnis,
    kenntnisDatum: eingabeDatum.nullable(),
    verstoss: dbPortalVerstoss,
    /** bei Dauerverstoß: Ende des Verstoßes (z. B. Vertragsende); null = noch nicht beendet */
    beendigung: eingabeDatum.nullable(),
    /** Zahlungszeitpunkt: Monatserster oder – bei Vorauszahlung je Quartal – Quartalserster */
    zahlung: z.enum(['monat', 'quartal']),
    /** Zeitraum einer Hemmung (§ 209 BGB: wird in keine Frist eingerechnet); null = keine */
    hemmung: z.object({ von: eingabeDatum, bis: eingabeDatum }).nullable(),
    /** Vergleichswerte Monatsentgelt netto in Cent (bis zu drei Szenarien, Eingabe GQL); leer = keine Schätzung */
    vergleichswerteCent: z.array(z.number().int().nonnegative()).max(3),
});
/** Abfrageparameter (Query-String); fehlende Werte → Standardannahmen */
export const dbPortalAnalyseAbfrage = z.object({
    stichtag: eingabeDatum.optional(),
    kenntnis: dbPortalKenntnis.optional(),
    kenntnisDatum: eingabeDatum.optional(),
    verstoss: dbPortalVerstoss.optional(),
    beendigung: eingabeDatum.optional(),
    zahlung: z.enum(['monat', 'quartal']).optional(),
    hemmungVon: eingabeDatum.optional(),
    hemmungBis: eingabeDatum.optional(),
    /** bis zu drei Vergleichswerte in Euro (Dezimalpunkt oder -komma), durch Semikolon getrennt */
    vergleichswert: z.string().regex(/^\d{1,6}([.,]\d{1,2})?(;\d{1,6}([.,]\d{1,2})?){0,2}$/).optional(),
});
export const dbPortalFristStatus = z.enum(['verjaehrt', 'laeuft', 'offen']);
export const dbPortalFrist = z.object({
    status: dbPortalFristStatus,
    /** letzter Tag, an dem der Anspruch nach den Annahmen noch nicht verjährt ist */
    ablauf: isoDatum.nullable(),
    grund: z.string(),
});
export const dbPortalEntgeltQuelle = z.enum(['komponenten', 'entgeltaenderung', 'entgelt', 'hergeleitet']);
/** Monatsentgelt netto aus einer Unterlage mit Geltungszeitraum (Monate, inklusive) */
export const dbPortalEntgeltBeleg = z.object({
    dokumentId: z.string().uuid(),
    dateiname: z.string(),
    art: dbPortalDokumentArt,
    quelle: dbPortalEntgeltQuelle,
    monatCent: z.number().int(),
    /** null = unbegrenzt (gilt „seit jeher“ bzw. bis zur nächsten Änderung) */
    von: isoMonat.nullable(),
    bis: isoMonat.nullable(),
    herleitung: z.string(),
    /** Tabellensumme stimmig bzw. Fundstelle belegt */
    sicher: z.boolean(),
    uebernommen: z.boolean(),
});
export const dbPortalEntgeltMonat = z.object({
    monat: isoMonat,
    cent: z.number().int().nullable(),
    quelle: dbPortalEntgeltQuelle.nullable(),
    dokumentId: z.string().uuid().nullable(),
    /** false: Lesefehler möglich oder letzter bekannter Wert fortgeschrieben */
    sicher: z.boolean(),
    fortgeschrieben: z.boolean(),
});
export const dbPortalEntgeltJahr = z.object({
    jahr: z.number().int(),
    monateBelegt: z.number().int(),
    monateSicher: z.number().int(),
    minCent: z.number().int().nullable(),
    medianCent: z.number().int().nullable(),
    maxCent: z.number().int().nullable(),
    /** Summe der belegten Monate (Jahreszahlung netto, soweit belegt) */
    summeCent: z.number().int().nullable(),
    quellen: z.array(dbPortalEntgeltQuelle),
});
const fristJahr = z.object({
    laeuft: z.number().int(),
    verjaehrt: z.number().int(),
    offen: z.number().int(),
    /** Ablauf für die letzte Zahlung des Jahres (Dezember) */
    ablauf: isoDatum.nullable(),
});
export const dbPortalVerjaehrungJahr = z.object({
    jahr: z.number().int(),
    monate: z.number().int(),
    bgb: fristJahr,
    gwb: fristJahr,
});
const anspruchStand = z.object({
    /** erster Zahlungsmonat, der nach den Annahmen am Stichtag nicht verjährt ist */
    offenAb: isoMonat.nullable(),
    /** nächster Ablauf einer laufenden Frist nach dem Stichtag */
    naechsterAblauf: isoDatum.nullable(),
    monateLaufend: z.number().int(),
    monateOffen: z.number().int(),
    monateVerjaehrt: z.number().int(),
});
export const dbPortalVerjaehrung = z.object({
    jahre: z.array(dbPortalVerjaehrungJahr),
    bgb: anspruchStand,
    gwb: anspruchStand,
    annahmen: z.array(z.string()),
});
const schaetzBetrag = z.object({
    monate: z.number().int(),
    monateOhneWert: z.number().int(),
    vonMonat: isoMonat.nullable(),
    bisMonat: isoMonat.nullable(),
    hauptforderungCent: z.number().int(),
    zinsenCent: z.number().int(),
});
export const dbPortalSchaetzung = z.object({
    vergleichswertCent: z.number().int(),
    /** nur Monate mit nicht verjährtem Anspruch (laufend oder offen) */
    bgb: schaetzBetrag,
    gwb: schaetzBetrag,
    /** alle belegten Monate ohne Verjährungsabzug (Obergrenze) */
    gesamt: schaetzBetrag,
    zinsen: z.object({ methode: z.string(), quelle: z.string(), abgerufen: isoDatum, ausserhalbTabelle: z.boolean() }),
});
export const dbPortalReifeStufe = z.enum(['bereit', 'teilweise', 'unzureichend']);
export const dbPortalReife = z.object({
    stufe: dbPortalReifeStufe,
    vertrag: z.boolean(),
    rechnungsjahre: z.number().int(),
    kernjahre: z.number().int(),
    monateBelegt: z.number().int(),
    kernmonate: z.number().int(),
    luecken: z.array(z.string()),
});
export const dbPortalStandortAnalyse = z.object({
    standort: z.object({
        id: z.string().uuid(),
        externeId: z.string().nullable(),
        name: z.string(),
        ort: z.string().nullable(),
        gebietSlug: z.string().nullable(),
        gebietName: z.string().nullable(),
        skalaWert: z.number().int().nullable(),
    }),
    parameter: dbPortalAnalyseParameter,
    kern: z.object({ von: z.number().int(), bis: z.number().int() }),
    zeitraum: z.object({ von: z.number().int(), bis: z.number().int() }),
    reife: dbPortalReife,
    belege: z.array(dbPortalEntgeltBeleg),
    monate: z.array(dbPortalEntgeltMonat),
    jahre: z.array(dbPortalEntgeltJahr),
    /** Median der sicheren Monatsentgelte im Kernzeitraum (Benchmark-Kennwert) */
    kennwertCent: z.number().int().nullable(),
    /** null = für diese Rolle nicht freigeschaltet */
    verjaehrung: dbPortalVerjaehrung.nullable(),
    /** je Vergleichswert ein Szenario; null = nicht freigeschaltet, leer = kein Vergleichswert eingegeben */
    schaetzungen: z.array(dbPortalSchaetzung).max(3).nullable(),
    hinweise: z.array(z.string()),
    stand: z.string(),
});
export const dbPortalBenchmarkStandort = z.object({
    id: z.string().uuid(),
    externeId: z.string().nullable(),
    name: z.string(),
    ort: z.string().nullable(),
    kennwertCent: z.number().int().nullable(),
    monateBelegt: z.number().int(),
    /** Abweichung vom Median des Gebiets in Prozent */
    abweichungProzent: z.number().nullable(),
    reife: dbPortalReifeStufe,
    /** erster nicht verjährter Zahlungsmonat (GWB) nach den Annahmen */
    gwbOffenAb: isoMonat.nullable(),
    bgbOffenAb: isoMonat.nullable(),
});
export const dbPortalBenchmarkGebiet = z.object({
    gebietSlug: z.string().nullable(),
    gebietName: z.string(),
    skalaWert: z.number().int().nullable(),
    standorte: z.number().int(),
    mitKennwert: z.number().int(),
    minCent: z.number().int().nullable(),
    medianCent: z.number().int().nullable(),
    maxCent: z.number().int().nullable(),
    eintraege: z.array(dbPortalBenchmarkStandort),
});
export const dbPortalAuswertung = z.object({
    parameter: dbPortalAnalyseParameter,
    kern: z.object({ von: z.number().int(), bis: z.number().int() }),
    reife: z.object({
        bereit: z.number().int(),
        teilweise: z.number().int(),
        unzureichend: z.number().int(),
        /** Bedingung für die Kurz-Einschätzung (Stufe 1 des Vereinbarungsentwurfs) */
        stufe1: z.object({ erreicht: z.boolean(), standorteBereit: z.number().int(), gebieteBereit: z.number().int(), bedingung: z.string() }),
    }),
    gebiete: z.array(dbPortalBenchmarkGebiet),
    gesamt: z.object({ mitKennwert: z.number().int(), minCent: z.number().int().nullable(), medianCent: z.number().int().nullable(), maxCent: z.number().int().nullable() }),
    annahmen: z.array(z.string()),
    stand: z.string(),
});
//# sourceMappingURL=db-portal-analyse.js.map